import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PrintDocumentButton, RecordDocuments } from '../components/PrintDocument';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, type OrderBlock, type PurchaseSummary } from '../api';
import { OrderDetail, type OrderData } from '../components/OrderDetail';
import { ItemImage } from '../components/ItemImage';
import { PurchaseForm, type PurchaseFormValues } from '../components/PurchaseForm';
import { canWrite, useAuth } from '../auth';
import { PurchasePayables } from './Bills';
import { Badge, Button, Card, ErrorMessage, Field, Input, Notice, PageHeader, Spinner, Stat, money, number, plural, unitMoney, when } from '../ui';

interface Detail {
  purchase: PurchaseSummary & {
    sellerContact: string | null;
    vatScheme: 'none' | 'standard' | 'margin';
    allocationMethod: 'by_value' | 'equal';
    notes: string | null;
  };
  stats: {
    batches: number;
    unique_lots: number;
    pieces_bought: number;
    pieces_remaining: number;
    stock_value: string;
    cost_allocated: string;
    cost_remaining: string;
    unpriced_entries: number;
  };
  batches: { id: number; name: string; status: string; plannedOnlineAt: string | null; entries: number; pieces: number }[];
  /** Present for a purchase order: its lines, receipts and charges. Null for a purchase made by hand. */
  order: OrderBlock | null;
}

export function PurchaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [online, setOnline] = useState('');

  const detail = useQuery({ queryKey: ['purchase', id], queryFn: () => api<Detail>(`/api/v1/purchases/${id}`) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['purchase', id] });
    void qc.invalidateQueries({ queryKey: ['purchases'] });
  };

  const save = useMutation({
    mutationFn: (v: PurchaseFormValues) => api(`/api/v1/purchases/${id}`, { method: 'PATCH', body: v }),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
  });
  const addBatch = useMutation({
    mutationFn: () =>
      api(`/api/v1/purchases/${id}/batches`, {
        body: { name: batchName.trim(), plannedOnlineAt: online ? new Date(online).toISOString() : null },
      }),
    onSuccess: () => {
      setBatchName('');
      setOnline('');
      refresh();
    },
  });
  const allocate = useMutation({
    mutationFn: () => api<{ allocation: { entries: number; allocatedTotal: string; frozenTotal: string; method: string } }>(`/api/v1/purchases/${id}/allocate-costs`, { method: 'POST', body: {} }),
    onSuccess: refresh,
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error || !detail.data) return <ErrorMessage error={detail.error} />;
  // A purchase order has its own screen: lines, receiving and what is still expected.
  const writableHere = canWrite(user);
  if (detail.data.order) {
    return (
      <>
        <OrderDetail data={detail.data as unknown as OrderData} />
        <div className="mt-4">
          <PurchasePayables purchaseId={detail.data.purchase.id} writable={writableHere} />
        </div>
        <div className="mt-4">
          <RecordDocuments recordType="purchase" recordId={detail.data.purchase.id} />
        </div>
      </>
    );
  }
  const { purchase: p, stats: s, batches } = detail.data;

  return (
    <>
      <PageHeader
        title={p.description || `Purchase ${p.id}`}
        subtitle={
          <>
            <Link to="/purchases" className="hover:underline">
              Purchases
            </Link>{' '}
            / bought {p.purchasedOn}
            {p.sellerName ? ` from ${p.sellerName}` : ''} for {p.isSystem ? 'an unknown price' : money(p.totalCost, p.currency)}
          </>
        }
        actions={!p.isSystem && p.source !== 'partout' && <Button variant="secondary" onClick={() => setEditing((v) => !v)}>{editing ? 'Cancel' : 'Edit'}</Button>}
      />
      <div className="space-y-4">
        {p.source === 'partout' && (
          <Notice>
            These are the parts from breaking up a set. Each part is costed at its share of what the set cost, so there is no price to edit or spread here.
          </Notice>
        )}
        <PurchasePayables purchaseId={p.id} writable={writableHere} />
        <RecordDocuments recordType="purchase" recordId={p.id} />
        {editing && (
          <Card title="Edit purchase">
            <PurchaseForm
              submitLabel="Save changes"
              busy={save.isPending}
              initial={{
                purchasedOn: p.purchasedOn,
                description: p.description,
                defaultCondition: p.defaultCondition,
                totalCost: String(Number(p.totalCost)),
                sellerName: p.sellerName,
                sellerContact: p.sellerContact,
                vatScheme: p.vatScheme,
                allocationMethod: p.allocationMethod,
                notes: p.notes,
              }}
              onSubmit={(v) => save.mutate(v)}
            />
            <div className="mt-3">
              <ErrorMessage error={save.error} />
            </div>
          </Card>
        )}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Stat label="Pieces bought" value={number(s.pieces_bought)} note={`${plural(s.unique_lots, 'different lot')}`} />
          <Stat label="Pieces left" value={number(s.pieces_remaining)} />
          <Stat label="Value left" value={money(s.stock_value, p.currency)} note={s.unpriced_entries > 0 ? `${plural(s.unpriced_entries, 'entry', 'entries')} without a price yet` : 'At your listed prices'} />
          <Stat label="Cost spread" value={money(s.cost_allocated, p.currency)} note={`of ${money(p.totalCost, p.currency)} paid`} />
          <Stat label="Cost of what is left" value={money(s.cost_remaining, p.currency)} />
        </div>

        {!p.isSystem && p.source !== 'partout' && (
          <Card
            title="Cost per piece"
            actions={
              <Button variant="secondary" disabled={allocate.isPending || s.pieces_bought === 0} onClick={() => allocate.mutate()}>
                {allocate.isPending ? 'Working…' : 'Spread the price now'}
              </Button>
            }
          >
            <p className="text-sm text-slate-600">
              The price paid is spread over the pieces in this purchase {p.allocationMethod === 'by_value' ? 'in proportion to their value' : 'equally'}, so each piece has a cost. Run this after you have added stock and set prices. Pieces already sold keep the cost they were sold at.
            </p>
            <div className="mt-3 space-y-2">
              <ErrorMessage error={allocate.error} />
              {allocate.data && (
                <Notice tone="green">
                  Spread {money(allocate.data.allocation.allocatedTotal, p.currency)} over {plural(allocate.data.allocation.entries, 'entry', 'entries')}.
                </Notice>
              )}
            </div>
          </Card>
        )}

        <Card title="Batches">
          <p className="mb-3 text-sm text-slate-600">
            Split a big purchase into batches so you can put its stock on sale a little at a time.
          </p>
          <ul className="divide-y divide-slate-100">
            {batches.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5 text-sm">
                <div>
                  <span className="font-medium">{b.name}</span>{' '}
                  <Badge tone={b.status === 'live' ? 'green' : b.status === 'scheduled' ? 'blue' : 'slate'}>{b.status}</Badge>
                  <div className="text-xs text-slate-500">
                    {plural(b.entries, 'entry', 'entries')} · {plural(b.pieces, 'piece')}
                    {b.plannedOnlineAt && ` · planned online ${when(b.plannedOnlineAt)}`}
                  </div>
                </div>
                <Link to={`/batches/${b.id}/add`}>
                  <Button>Add stock</Button>
                </Link>
              </li>
            ))}
            {batches.length === 0 && <li className="py-2 text-sm text-slate-500">No batches yet. Create one to start adding stock.</li>}
          </ul>
          <form
            className="mt-4 flex flex-wrap items-end gap-3 border-t border-slate-100 pt-4"
            onSubmit={(e) => {
              e.preventDefault();
              addBatch.mutate();
            }}
          >
            <Field label="New batch">
              <Input required placeholder="e.g. First half" value={batchName} onChange={(e) => setBatchName(e.target.value)} />
            </Field>
            <Field label="Planned online (optional)" hint="Used by the publishing step in a later phase.">
              <Input type="datetime-local" value={online} onChange={(e) => setOnline(e.target.value)} />
            </Field>
            <Button type="submit" variant="secondary" disabled={!batchName.trim() || addBatch.isPending}>
              Create batch
            </Button>
          </form>
          <div className="mt-2">
            <ErrorMessage error={addBatch.error} />
          </div>
        </Card>
      </div>
    </>
  );
}

/** Printer-friendly supplier receipt or blind incoming-goods count sheet. */
export function PurchaseDocument() {
  const { id, kind } = useParams();
  const detail = useQuery({ queryKey: ['purchase-document', id], queryFn: () => api<Detail>(`/api/v1/purchases/${id}`) });
  if (detail.isPending) return <Spinner />;
  if (detail.error || !detail.data || !detail.data.order) return <ErrorMessage error={detail.error ?? new Error('This purchase has no order lines to print.')} />;
  const { purchase: p, order } = detail.data;
  const receipt = kind === 'receipt';
  return <main data-document className="mx-auto max-w-3xl bg-white p-8 print:max-w-none"><div className="mb-8 flex items-start justify-between"><div><h1 className="text-2xl font-bold">BrickLoop</h1><h2 className="mt-2 text-xl">{receipt ? 'Purchase Receipt' : 'Incoming-Goods Count Sheet'}</h2><p className="text-sm text-slate-600">{p.description || `Purchase ${p.id}`} · ordered {p.purchasedOn}</p></div><PrintDocumentButton recordType="purchase" recordId={p.id} kind={receipt ? 'receipt' : 'count'} /></div><div className="mb-6 grid gap-3 text-sm sm:grid-cols-2"><div><strong>Supplier</strong><div>{p.sellerName ?? '—'}</div></div><div><strong>Order status</strong><div>{p.status.replace('_', ' ')}</div>{receipt && <><strong className="mt-3 block">Order total</strong><div>{money(p.totalCost, p.currency)}</div></>}</div></div>{!receipt && <p className="mb-4 text-sm text-slate-500">Write what physically arrived. Expected quantities are intentionally not shown.</p>}<table className="data w-full text-sm"><thead><tr><th className="text-center">Item</th><th className="text-center">Colour / Condition</th>{receipt ? <><th className="text-center">Ordered</th><th className="text-center">Received</th><th className="text-center">Unit Price</th></> : <><th className="text-center">Counted Quantity</th><th className="text-center">Bin</th></>}</tr></thead><tbody>{order.lines.map((line) => <tr key={line.id}><td><div className="flex gap-3"><ItemImage eager className="size-12" itemType={line.itemType} itemNo={line.itemNo} colorId={line.colorId} /><div><strong>{line.itemNo}</strong><div className="text-xs text-slate-500">{line.itemName}</div></div></div></td><td>{line.colorName} · {line.condition === 'N' ? 'New' : 'Used'}</td>{receipt ? <><td className="text-right">{line.qtyOrdered}</td><td className="text-right">{line.qtyReceived}</td><td className="text-right">{unitMoney(line.unitPrice, p.currency)}</td></> : <><td className="text-center"><span aria-label={`Blank quantity box for ${line.itemNo}`} className="inline-block h-10 w-24 border-2 border-slate-800" /></td><td className="text-center"><span aria-label={`Blank bin box for ${line.itemNo}`} className="inline-block h-10 w-28 border-2 border-slate-800" /></td></>}</tr>)}</tbody></table></main>;
}
